/**
 * Training agent sessions (TRA-1327): thin wrappers around the
 * orval-generated hooks, plus a session's live event stream.
 *
 * Staff only. The api 403s everyone else, so callers gate on isEmployee.
 * Same unwrap pattern as useTrainingRuns.ts (orval v8 FETCH client returns
 * {data, status, headers}).
 */

import { useQueryClient } from '@tanstack/react-query'
import type { UseMutationResult } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { type ApiRequestError, apiHeaders, apiUrl, getApiErrorDetail } from '@/api/client'
import {
  getListSessionsApiTrainingAgentSessionsGetQueryKey,
  getStreamSessionEventsApiTrainingAgentSessionsSessionIdEventsGetUrl,
  useCreateSessionApiTrainingAgentSessionsPost,
  useDecideApprovalApiTrainingAgentSessionsSessionIdApprovalsApprovalIdPost,
  useDecideLaunchRequestApiTrainingAgentSessionsSessionIdLaunchRequestsRequestIdPost,
  useGetSessionApiTrainingAgentSessionsSessionIdGet,
  useListSessionsApiTrainingAgentSessionsGet,
  useSendMessageApiTrainingAgentSessionsSessionIdMessagesPost,
  useStopSessionApiTrainingAgentSessionsSessionIdStopPost,
} from '@/api/generated/training-agent/training-agent'
import type { createSessionApiTrainingAgentSessionsPostResponse } from '@/api/generated/training-agent/training-agent'
import { TrainingAgentEventViewType } from '@/api/generated/model'
import type {
  TrainingAgentDecision,
  TrainingAgentEventView,
  TrainingAgentLaunchDecision,
  TrainingAgentMessageCreate,
  TrainingAgentSessionCreate,
  TrainingAgentSessionView,
  TrainingAgentSessionsResponse,
} from '@/api/generated/model'
import { getListTrainingRunsApiTrainingRunsGetQueryKey } from '@/api/generated/training-runs/training-runs'
import { StreamCursor } from '@/components/training/sessions/transcript'
import { readErrorDetail, readSseFrames } from '@/lib/sse'

/**
 * How often the list refreshes while the tab shows it: a session opened in
 * another tab or by someone else appears, and a live one's status and cost
 * follow it. The app's queries otherwise stay fresh for five minutes and
 * skip refetching on focus (main.tsx).
 */
const REFETCH_MS = 10_000
/** Wait this long before reopening a stream that failed or brought nothing. */
const RETRY_MS = 3_000

interface UnwrappedQuery<TData> {
  data: TData | undefined
  isLoading: boolean
  error: ApiRequestError | null
  isError: boolean
}

export function useAgentSessions(enabled: boolean): UnwrappedQuery<TrainingAgentSessionsResponse> {
  const query = useListSessionsApiTrainingAgentSessionsGet(undefined, {
    query: { enabled, refetchInterval: REFETCH_MS },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/** One session by id, for a link to a session outside the list's first page. */
export function useAgentSession(
  sessionId: string,
  enabled: boolean,
): UnwrappedQuery<TrainingAgentSessionView> {
  const query = useGetSessionApiTrainingAgentSessionsSessionIdGet(sessionId, {
    query: {
      enabled,
      // A live session refreshes like the list, and an ended one never changes.
      refetchInterval: (current) => {
        const body = current.state.data
        return body?.status === 200 && body.data.ended_at === null ? REFETCH_MS : false
      },
    },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

function useRefreshSessions(): () => void {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({
      queryKey: getListSessionsApiTrainingAgentSessionsGetQueryKey(),
    })
  }
}

/** The dialog shows the api's refusal itself (a live session, a stack without config). */
export function useCreateAgentSession(): UseMutationResult<
  createSessionApiTrainingAgentSessionsPostResponse,
  ApiRequestError,
  { data: TrainingAgentSessionCreate }
> {
  const refresh = useRefreshSessions()
  return useCreateSessionApiTrainingAgentSessionsPost({ mutation: { onSuccess: refresh } })
}

export function useSendAgentMessage(): UseMutationResult<
  unknown,
  ApiRequestError,
  { sessionId: string; data: TrainingAgentMessageCreate }
> {
  return useSendMessageApiTrainingAgentSessionsSessionIdMessagesPost({
    mutation: {
      onError: (error) => {
        toast.error(getApiErrorDetail(error) ?? 'The message was not sent. Try again.')
      },
    },
  })
}

export function useStopAgentSession(): UseMutationResult<
  unknown,
  ApiRequestError,
  { sessionId: string }
> {
  const refresh = useRefreshSessions()
  return useStopSessionApiTrainingAgentSessionsSessionIdStopPost({
    mutation: {
      onSuccess: refresh,
      onError: (error) => {
        toast.error(getApiErrorDetail(error) ?? 'The session did not stop. Try again.')
      },
    },
  })
}

export function useDecideAgentApproval(): UseMutationResult<
  unknown,
  ApiRequestError,
  { sessionId: string; approvalId: string; data: TrainingAgentDecision }
> {
  return useDecideApprovalApiTrainingAgentSessionsSessionIdApprovalsApprovalIdPost({
    mutation: {
      onError: (error) => {
        toast.error(getApiErrorDetail(error) ?? 'The decision was not saved. Try again.')
      },
    },
  })
}

/** Approve launches the run as the approver (TRA-1326); the outcome arrives in the stream. */
export function useDecideLaunchRequest(): UseMutationResult<
  unknown,
  ApiRequestError,
  { sessionId: string; requestId: string; data: TrainingAgentLaunchDecision }
> {
  const queryClient = useQueryClient()
  return useDecideLaunchRequestApiTrainingAgentSessionsSessionIdLaunchRequestsRequestIdPost({
    mutation: {
      // An approval starts a run, which the Launches list on the Runs tab shows.
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getListTrainingRunsApiTrainingRunsGetQueryKey(),
        })
      },
      onError: (error) => {
        toast.error(getApiErrorDetail(error) ?? 'The decision was not saved. Try again.')
      },
    },
  })
}

export type StreamState = 'connecting' | 'open' | 'retrying' | 'closed'

export interface SessionEvents {
  events: TrainingAgentEventView[]
  state: StreamState
  /** Why the stream is retrying or closed early, for the page to show. */
  error: string | null
}

interface StreamSink {
  onEvents: (events: TrainingAgentEventView[]) => void
  onState: (state: StreamState, error: string | null) => void
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms)
    signal.addEventListener('abort', () => {
      clearTimeout(timer)
      resolve()
    })
  })
}

class StreamRefused extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
  }
}

/** A frame the api sent as a TrainingAgentEventView, checked before the page trusts it. */
function isEventView(frame: unknown): frame is TrainingAgentEventView {
  if (typeof frame !== 'object' || frame === null) return false
  const { seq, type, payload } = frame as Record<string, unknown>
  return (
    typeof seq === 'number' &&
    Object.values(TrainingAgentEventViewType).some((known) => known === type) &&
    typeof payload === 'object' &&
    payload !== null
  )
}

/**
 * One read of the stream after the cursor, moving the cursor with each
 * event, so a read that drops midway resumes where it stopped. Throws when
 * the api refuses the stream.
 */
async function readOnce(
  sessionId: string,
  cursor: StreamCursor,
  signal: AbortSignal,
  sink: StreamSink,
): Promise<void> {
  const path = getStreamSessionEventsApiTrainingAgentSessionsSessionIdEventsGetUrl(sessionId, {
    after: cursor.seq,
  })
  const response = await fetch(apiUrl(path), { headers: apiHeaders(), signal })
  if (!response.ok || response.body === null) {
    throw new StreamRefused(await readErrorDetail(response), response.status)
  }
  sink.onState('open', null)
  await readSseFrames(response.body, (frame) => {
    if (!isEventView(frame)) {
      console.error('Training agent stream: a frame is not a session event', frame)
      return
    }
    if (frame.seq <= cursor.seq) return
    cursor.read(frame)
    sink.onEvents([frame])
  })
}

/**
 * Follow a session's events until the cursor is settled: the session is
 * over and every approved launch has its outcome. The api ends each stream
 * after a while or once the session is over, so a clean end reopens it
 * from the cursor, at once if it brought events. A dropped connection
 * retries after RETRY_MS. A refusal other than a 5xx closes it, since
 * retrying cannot change it.
 */
async function follow(sessionId: string, signal: AbortSignal, sink: StreamSink): Promise<void> {
  const cursor = new StreamCursor()
  // A function, since readOnce and an abort change both while the loop waits.
  const done = (): boolean => signal.aborted || cursor.settled
  while (!done()) {
    const before = cursor.seq
    try {
      await readOnce(sessionId, cursor, signal, sink)
      if (cursor.seq === before && !done()) await sleep(RETRY_MS, signal)
    } catch (caught) {
      if (signal.aborted) return
      if (caught instanceof StreamRefused && caught.status < 500) {
        sink.onState('closed', caught.message)
        return
      }
      sink.onState(
        'retrying',
        'The connection to this session dropped, and the page will retry in a few seconds.',
      )
      await sleep(RETRY_MS, signal)
    }
  }
  if (!signal.aborted) sink.onState('closed', null)
}

/**
 * The session's events, replayed from the first and then live. Mount it
 * keyed by session id: a new session starts from an empty list.
 */
export function useSessionEvents(sessionId: string): SessionEvents {
  const [events, setEvents] = useState<TrainingAgentEventView[]>([])
  const [stream, setStream] = useState<{ state: StreamState; error: string | null }>({
    state: 'connecting',
    error: null,
  })

  useEffect(() => {
    const controller = new AbortController()
    void follow(sessionId, controller.signal, {
      // Keyed by seq, so a replayed event is never shown twice.
      onEvents: (batch) => {
        setEvents((current) => {
          const last = current[current.length - 1]?.seq ?? -1
          const fresh = batch.filter((event) => event.seq > last)
          return fresh.length === 0 ? current : [...current, ...fresh]
        })
      },
      onState: (state, error) => {
        setStream({ state, error })
      },
    })
    return () => {
      controller.abort()
    }
  }, [sessionId])

  return { events, ...stream }
}
